import { useEffect, useMemo, useRef, useState } from "react";
import { useController, useForm, useWatch } from "react-hook-form";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Select from "../../../components/Form/Select";
import TextField from "../../../components/Form/TextField";
import Button from "../../../components/Buttons/Button";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import {
  startListenerMergedFonts,
  startUploadAdvertiserFont,
  stopListenerMergedFonts,
} from "../../../actions/fonts";
import {
  ADD_FONT_VALUE,
  FONT_KIND,
  buildFontSelection,
  getDefaultFace,
  getFontMimeType,
  loadFontFaces,
  normalizeFontName,
  resolveLayerFont,
  validateFontName,
} from "../utils/fonts";
import { parseFontFile } from "../utils/parseFontFile";
import { groupFontUploadEntries } from "../utils/groupFontUploads";
import { SKIP_PERSIST_ITALIC } from "../utils/resolveFontFamilyIdentity";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  FIELD_REQUIRED,
  TEMPLATES_ADD_FONT,
  TEMPLATES_ADD_FONT_CHANGE_FILE,
  TEMPLATES_ADD_FONT_ERROR,
  TEMPLATES_ADD_FONT_INVALID_FILE,
  TEMPLATES_ADD_FONT_INVALID_NAME,
  TEMPLATES_ADD_FONT_NAME,
  TEMPLATES_ADD_FONT_PARSE_FAILED,
  TEMPLATES_ADD_FONT_PARTIAL_ERROR,
  TEMPLATES_ADD_FONT_SELECT_FILE,
  TEMPLATES_ADD_FONT_SUMMARY,
  TEMPLATES_ADD_FONT_SUMMARY_FAMILY,
  TEMPLATES_ADD_FONT_SUMMARY_HINT,
  TEMPLATES_ADD_FONT_SUMMARY_KIND,
  TEMPLATES_ADD_FONT_SUMMARY_STATIC,
  TEMPLATES_ADD_FONT_SUMMARY_STYLE,
  TEMPLATES_ADD_FONT_SUMMARY_STYLES,
  TEMPLATES_ADD_FONT_TITLE,
  TEMPLATES_ADD_FONT_VARIABLE,
  TEMPLATES_FONT_FAMILY,
  TEMPLATES_UNAUTHORIZED,
} from "../../../i18n/keysTranslations";

const optionalRules = { required: false };
const EMPTY_ADD_FONT_VALUES = {};

const summaryChipSx = {
  backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
};

const familyNameFromFile = (file) => {
  const base = String(file?.name || "")
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalizeFontName({ name: base });
};

const SummaryRow = ({ label, value, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontWeight: 500 }}
    >
      {label}
    </Typography>
    {children || (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    )}
  </Box>
);

const FamilySummary = ({ group, t }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
    <SummaryRow
      label={t(TEMPLATES_ADD_FONT_SUMMARY_FAMILY)}
      value={group.familyName}
    />
    <SummaryRow
      label={t(TEMPLATES_ADD_FONT_SUMMARY_KIND)}
      value={t(
        group.kind === FONT_KIND.VARIABLE
          ? TEMPLATES_ADD_FONT_VARIABLE
          : TEMPLATES_ADD_FONT_SUMMARY_STATIC
      )}
    />
    {group.styles.length ? (
      <SummaryRow
        label={t(
          group.kind === FONT_KIND.VARIABLE || group.styles.length > 1
            ? TEMPLATES_ADD_FONT_SUMMARY_STYLES
            : TEMPLATES_ADD_FONT_SUMMARY_STYLE
        )}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {group.styles.map((styleName) => (
            <Chip
              key={styleName}
              label={styleName}
              size="small"
              sx={summaryChipSx}
            />
          ))}
        </Box>
      </SummaryRow>
    ) : null}
  </Box>
);

const uploadErrorKey = (reason) => {
  switch (reason) {
    case "unauthorized":
      return TEMPLATES_UNAUTHORIZED;
    case "invalidFile":
      return TEMPLATES_ADD_FONT_INVALID_FILE;
    case "invalidName":
      return TEMPLATES_ADD_FONT_INVALID_NAME;
    default:
      return TEMPLATES_ADD_FONT_ERROR;
  }
};

const FontPreviewLabel = ({ familyName, fontWeight, italic, children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: `"${familyName}"`,
      fontWeight: fontWeight || 400,
      fontStyle: italic ? "italic" : "normal",
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 1.2,
      // Optical vertical centering in filled Select value/options.
      transform: "translateY(2px)",
    }}
  >
    {children}
  </Box>
);

//eslint-disable-next-line
export const useMergedFonts = ({ enabled = true } = {}) => {
  const dispatch = useDispatchApp();
  const listenerRefs = useRef({ commonRef: null, advertiserRef: null });
  const [fonts, setFonts] = useState([]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    dispatch(
      startListenerMergedFonts({
        onUpdatedDataCallback: (nextFonts) => {
          setFonts(nextFonts);
          loadFontFaces({ families: nextFonts });
        },
        onReferenceAvailable: (refs) => {
          listenerRefs.current = refs;
        },
      })
    );

    return () => {
      dispatch(stopListenerMergedFonts({ listenerRefs: listenerRefs.current }));
    };
  }, [dispatch, enabled]);

  return fonts;
};

const AddFontModal = ({
  modalOpen,
  disabled,
  onCloseModal,
  onUploaded,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const fileInputRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_ADD_FONT_VALUES,
  });

  useEffect(() => {
    if (!modalOpen) return;
    reset(EMPTY_ADD_FONT_VALUES);
    setEntries([]);
    setIsParsing(false);
  }, [modalOpen, reset]);

  const grouped = useMemo(
    () => groupFontUploadEntries(entries),
    [entries]
  );

  const onClose = () => {
    if (isUploading) return;
    onCloseModal();
  };

  const onPickFiles = async (files = []) => {
    if (!files.length) return;

    const valid = [];
    files.forEach((file) => {
      if (getFontMimeType({ file })) valid.push(file);
    });
    if (valid.length !== files.length) {
      SimpleAlert({
        title: t(TEMPLATES_ADD_FONT_INVALID_FILE),
        icon: "error",
      });
    }
    if (!valid.length) {
      setEntries([]);
      reset({});
      return;
    }

    setEntries([]);
    setIsParsing(true);
    const nextEntries = await Promise.all(
      valid.map(async (file, index) => {
        const parsed = await parseFontFile({ file });
        return {
          id: `file${index}`,
          file,
          parsed,
          name:
            parsed.ok && parsed.familyName
              ? parsed.familyName
              : familyNameFromFile(file),
        };
      })
    );
    setIsParsing(false);
    setEntries(nextEntries);
    const names = {};
    nextEntries.forEach((entry) => {
      if (!entry.parsed?.ok) names[entry.id] = entry.name;
    });
    reset(names);
  };

  const onSubmit = async (values) => {
    if (!entries.length || isParsing) return;

    const uploads = [];
    grouped.families.forEach((group) => {
      group.entries.forEach((entry) => {
        if (entry.parsed?.skipPersist === SKIP_PERSIST_ITALIC) return;
        uploads.push({
          name: group.familyName,
          file: entry.file,
          parsed: entry.parsed,
        });
      });
    });

    let hasNameError = false;
    grouped.unparsed.forEach((entry) => {
      const nextName = normalizeFontName({ name: values[entry.id] });
      const nameError = validateFontName({ name: nextName });
      if (nameError === "required") {
        setError(entry.id, {
          type: "required",
          message: t(FIELD_REQUIRED),
        });
        hasNameError = true;
        return;
      }
      if (nameError === "invalidName") {
        setError(entry.id, {
          type: "validate",
          message: t(TEMPLATES_ADD_FONT_INVALID_NAME),
        });
        hasNameError = true;
        return;
      }
      if (!getFontMimeType({ file: entry.file })) {
        SimpleAlert({
          title: t(TEMPLATES_ADD_FONT_INVALID_FILE),
          icon: "error",
        });
        hasNameError = true;
        return;
      }
      uploads.push({
        name: nextName,
        file: entry.file,
        parsed: null,
      });
    });
    if (hasNameError) return;

    if (!uploads.length) {
      onCloseModal();
      return;
    }

    setIsUploading(true);
    const succeeded = [];
    let firstFailure = null;
    for (const item of uploads) {
      const response = await dispatch(
        startUploadAdvertiserFont({
          name: item.name,
          file: item.file,
          parsed: item.parsed,
        })
      );
      if (response.ok) {
        succeeded.push(response.data);
      } else if (!firstFailure) {
        firstFailure = response;
      }
    }
    setIsUploading(false);

    if (succeeded.length) {
      onUploaded(succeeded);
    }
    if (firstFailure) {
      SimpleAlert({
        title: t(
          succeeded.length
            ? TEMPLATES_ADD_FONT_PARTIAL_ERROR
            : uploadErrorKey(firstFailure.reason)
        ),
        icon: "error",
      });
      if (!succeeded.length) return;
    }
    onCloseModal();
  };

  const canSave =
    entries.length > 0 && !isParsing && !disabled && !isUploading;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      title={t(TEMPLATES_ADD_FONT_TITLE)}
      ariaLabel={t(TEMPLATES_ADD_FONT_TITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".ttf,.otf,.woff,.woff2"
          multiple
          hidden
          onChange={(event) => {
            const nextFiles = Array.from(event.target.files || []);
            event.target.value = "";
            onPickFiles(nextFiles);
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            type="button"
            color="secondary"
            disabled={disabled || isUploading || isParsing}
            onClick={() => fileInputRef.current?.click()}
          >
            {t(
              entries.length
                ? TEMPLATES_ADD_FONT_CHANGE_FILE
                : TEMPLATES_ADD_FONT_SELECT_FILE
            )}
          </Button>
          {!entries.length ? (
            <Typography variant="body2" color="text.secondary">
              {t(TEMPLATES_ADD_FONT_SUMMARY_HINT)}
            </Typography>
          ) : null}
        </Box>

        {isParsing ? (
          <Typography variant="body2" color="text.secondary">
            …
          </Typography>
        ) : null}

        {grouped.families.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t(TEMPLATES_ADD_FONT_SUMMARY)}
            </Typography>
            {grouped.families.map((group) => (
              <Box
                key={group.key}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  pb: grouped.families.length > 1 ? 1.5 : 0,
                  borderBottom:
                    grouped.families.length > 1
                      ? `1px solid ${INPUT_COLOR_BORDER}`
                      : "none",
                  "&:last-of-type": { borderBottom: 0, pb: 0 },
                }}
              >
                <FamilySummary group={group} t={t} />
              </Box>
            ))}
          </Box>
        ) : null}

        {grouped.unparsed.map((entry, index) => (
          <Box
            key={entry.id}
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              {entry.file.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(TEMPLATES_ADD_FONT_PARSE_FAILED)}
            </Typography>
            <TextField
              name={entry.id}
              control={control}
              errors={errors}
              label={t(TEMPLATES_ADD_FONT_NAME)}
              variant="filled"
              fullWidth
              autoFocus={index === 0}
              disabled={disabled || isUploading}
              rules={{
                required: { value: true, message: t(FIELD_REQUIRED) },
              }}
            />
          </Box>
        ))}
      </Box>
      <DialogActions
        sx={{
          px: { xs: 3, sm: 5 },
          pb: 3,
          mt: 0.5,
        }}
      >
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onClose,
            disabled: isUploading,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            onClick: handleSubmit(onSubmit),
            loading: isUploading,
            disabled: !canSave,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

const FontFamilySelect = ({
  control,
  errors,
  disabled = false,
  name = "fontAssetRef",
  label,
  fonts: fontsFromParent,
  required = false,
  onChangeField,
}) => {
  const { t } = useTranslationApp();
  const ownsListener = fontsFromParent === undefined;
  const listenedFonts = useMergedFonts({ enabled: ownsListener });
  const [isAddOpen, setIsAddOpen] = useState(false);
  const { field } = useController({
    control,
    name,
    rules: required
      ? { required: { value: true, message: t(FIELD_REQUIRED) } }
      : optionalRules,
  });
  const currentValue = useWatch({ control, name }) || "";
  const fontFaceRef = useWatch({ control, name: "fontFaceRef" }) || "";
  const fontWeight = useWatch({ control, name: "fontWeight" });
  const families = useMemo(
    () => (ownsListener ? listenedFonts : fontsFromParent || []),
    [ownsListener, listenedFonts, fontsFromParent]
  );

  const resolved = useMemo(
    () =>
      resolveLayerFont({
        families,
        fontAssetRef: currentValue,
        fontFaceRef,
        fontWeight,
      }),
    [currentValue, families, fontFaceRef, fontWeight]
  );
  const selectedFamilyName = resolved?.familyName || currentValue;

  const emitSelection = (family) => {
    const selection = buildFontSelection({
      family,
      face: getDefaultFace(family),
    });
    field.onChange(selection.fontAssetRef);
    onChangeField?.({ key: "fontSelection", value: selection });
  };

  const options = useMemo(() => {
    const fontOptions = families.map((family) => ({
      value: family.familyName,
      label: (
        <FontPreviewLabel familyName={family.familyName}>
          {family.familyName}
        </FontPreviewLabel>
      ),
    }));
    if (
      currentValue &&
      currentValue !== ADD_FONT_VALUE &&
      !fontOptions.some((option) => option.value === currentValue)
    ) {
      fontOptions.unshift({
        value: currentValue,
        label: (
          <FontPreviewLabel familyName={selectedFamilyName || currentValue}>
            {selectedFamilyName || currentValue}
          </FontPreviewLabel>
        ),
      });
    }
    return [
      { value: ADD_FONT_VALUE, label: t(TEMPLATES_ADD_FONT) },
      ...fontOptions,
    ];
  }, [currentValue, families, selectedFamilyName, t]);

  return (
    <>
      <Select
        control={control}
        errors={errors}
        name={name}
        label={label || t(TEMPLATES_FONT_FAMILY)}
        variant="filled"
        fullWidth
        displayEmpty
        disabled={disabled}
        rules={
          required
            ? { required: { value: true, message: t(FIELD_REQUIRED) } }
            : optionalRules
        }
        options={options}
        sx={{
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
          },
        }}
        renderValue={(selected) => {
          if (!selected || selected === ADD_FONT_VALUE) return "";
          const display = selected === currentValue ? selectedFamilyName : selected;
          return (
            <FontPreviewLabel familyName={display}>{display}</FontPreviewLabel>
          );
        }}
        validationChange={(value) => {
          if (value !== ADD_FONT_VALUE) return true;
          if (!disabled) setIsAddOpen(true);
          return false;
        }}
        actionsChange={(value) => {
          const family = families.find((item) => item.familyName === value);
          if (family) {
            emitSelection(family);
            return;
          }
          field.onChange(value);
          onChangeField?.({
            key: "fontSelection",
            value: {
              fontAssetRef: value,
              fontFaceRef: "",
              fontWeight: 400,
            },
          });
        }}
      />
      <AddFontModal
        modalOpen={isAddOpen}
        disabled={disabled}
        onCloseModal={() => setIsAddOpen(false)}
        onUploaded={(uploads) => {
          const list = Array.isArray(uploads) ? uploads : [uploads];
          const nextFamilies = list
            .map((item) => item?.family)
            .filter(Boolean);
          if (nextFamilies.length) {
            loadFontFaces({ families: nextFamilies });
          }
          const first = list[0];
          if (!first) return;
          emitSelection(
            first.family || {
              familyName: first.familyName || first.name,
            }
          );
        }}
      />
    </>
  );
};

export default FontFamilySelect;
